/**
 * <mr-announcement-bar> — rotating announcement messages.
 *
 * Desktop shows every message at once, so there is nothing to rotate; the
 * component only takes over below the breakpoint the section sets on
 * data-rotate-below. Rotation stops for prefers-reduced-motion, while the bar
 * has focus inside it, and while the pointer is over it.
 *
 * Markup contract (see sections/mr-announcement-bar.liquid).
 */
class MrAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.messages = Array.from(this.querySelectorAll('[data-mr-message]'));
    if (this.messages.length < 2) return;

    this.index = 0;
    this.paused = false;
    this.interval = Number(this.dataset.interval || 5) * 1000;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.widthQuery = window.matchMedia(`(max-width: ${this.dataset.rotateBelow || '749px'})`);

    this.querySelector('[data-mr-prev]')?.addEventListener('click', () => this.#go(-1));
    this.querySelector('[data-mr-next]')?.addEventListener('click', () => this.#go(1));

    this.onEnter = () => this.#pause();
    this.onLeave = () => this.#resume();
    this.addEventListener('focusin', this.onEnter);
    this.addEventListener('focusout', this.onLeave);
    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('mouseleave', this.onLeave);

    this.onConditions = () => {
      this.#render();
      this.#restart();
    };
    this.motionQuery.addEventListener('change', this.onConditions);
    this.widthQuery.addEventListener('change', this.onConditions);

    this.#render();
    this.#restart();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.removeEventListener('focusin', this.onEnter);
    this.removeEventListener('focusout', this.onLeave);
    this.removeEventListener('mouseenter', this.onEnter);
    this.removeEventListener('mouseleave', this.onLeave);
    this.motionQuery?.removeEventListener('change', this.onConditions);
    this.widthQuery?.removeEventListener('change', this.onConditions);
  }

  get #shouldRotate() {
    return this.widthQuery.matches && !this.motionQuery.matches && !this.paused;
  }

  #restart() {
    clearInterval(this.timer);
    if (!this.#shouldRotate) return;
    this.timer = setInterval(() => this.#go(1), this.interval);
  }

  #pause() {
    this.paused = true;
    clearInterval(this.timer);
  }

  #resume() {
    this.paused = false;
    this.#restart();
  }

  /** Manual navigation restarts the clock so the new message gets a full turn. */
  #go(direction) {
    const count = this.messages.length;
    this.index = (this.index + direction + count) % count;
    this.#render();
    this.#restart();
  }

  #render() {
    // Above the breakpoint every message is on screen, so none of them is hidden.
    const stacked = this.widthQuery.matches;

    this.messages.forEach((message, index) => {
      const current = index === this.index;
      message.toggleAttribute('data-active', current);
      message.setAttribute('aria-hidden', stacked && !current ? 'true' : 'false');
    });
  }
}

if (!customElements.get('mr-announcement-bar')) {
  customElements.define('mr-announcement-bar', MrAnnouncementBar);
}
